import { ButtonContent } from '@/components/shared/button-content';
import { PageButtonLink } from '@/components/shared/page-button-link';
import { Button } from '@/components/ui/button';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { useIsPreviewMode } from '@/hooks/use-is-preview-mode';
import { useSiteButtons } from '@/hooks/use-site-buttons';
import {
  getButtonHref,
  getButtonRel,
  getButtonTarget,
  isInternalCmsPageButtonLink,
} from '@/lib/blocks/button-links';
import { cn } from '@/lib/utils';
import type { ButtonConfig, ButtonStyle } from '@/types/button';

interface ButtonBlockProps extends Omit<ButtonConfig, 'text'> {
  text?: string;
}

const BUTTON_STYLE_TO_PROPS: Record<ButtonStyle, { colorScheme: 'primary' | 'secondary' }> = {
  primary: { colorScheme: 'primary' },
  secondary: { colorScheme: 'secondary' },
};

export function ButtonBlock({
  text = 'Click Here',
  link,
  style = 'primary',
  icon,
  iconPlacement,
  linkType,
  internalPageId,
  openInNewTab,
  nofollow,
  resolvedLink,
}: ButtonBlockProps) {
  const isPageEditor = useIsPageEditor();
  const isPreview = useIsPreviewMode();
  const { shape, buttonStyle: siteButtonStyle } = useSiteButtons();
  const styleProps = BUTTON_STYLE_TO_PROPS[style] || BUTTON_STYLE_TO_PROPS.primary;
  const buttonClassName = cn(
    'shrink min-w-0 max-w-full min-h-9 h-auto whitespace-normal break-words text-center',
    isPageEditor && !isPreview && 'pointer-events-none',
  );
  const href = getButtonHref(
    {
      link,
      linkType,
      internalPageId,
      resolvedLink,
    },
    { preview: isPreview },
  );
  const target = getButtonTarget({ openInNewTab });
  const rel = getButtonRel({ openInNewTab, nofollow });

  const sharedProps = {
    variant: 'solid',
    colorScheme: styleProps.colorScheme,
    shape,
    buttonStyle: siteButtonStyle,
    className: buttonClassName,
  } satisfies React.ComponentProps<typeof Button>;
  const content = <ButtonContent text={text} icon={icon} iconPlacement={iconPlacement} />;

  let buttonNode: React.ReactNode;

  if (!href) {
    buttonNode = <Button {...sharedProps}>{content}</Button>;
  } else {
    const isPageLink = isInternalCmsPageButtonLink({ linkType, internalPageId });
    if (isPageLink) {
      buttonNode = (
        <Button asChild {...sharedProps}>
          <PageButtonLink
            href={href}
            internalPageId={internalPageId}
            preventNavigation={isPageEditor && !isPreview}
          >
            {content}
          </PageButtonLink>
        </Button>
      );
    } else {
      buttonNode = (
        <Button asChild {...sharedProps}>
          <a
            href={href}
            target={target}
            rel={rel}
            onClick={(event) => {
              if (isPageEditor) {
                event.preventDefault();
                return;
              }
            }}
          >
            {content}
          </a>
        </Button>
      );
    }
  }

  return buttonNode;
}
